import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Modal from '../components/ui/Modal';
import Spinner from '../components/ui/Spinner';
import { Plus, Users, AlertCircle, Mail, Check, UserX, UserCheck } from 'lucide-react';
import {
  fetchTeamMembers,
  inviteTeamMember,
  fetchTeamMemberDetails,
  updateTeamMember,
  deactivateTeamMember,
  reactivateTeamMember,
  resendInvite
} from '../services/api';
import toast from 'react-hot-toast';
import { PERMISSIONS } from '../utils/permissions';
import { useAuthStore } from '../store/useAuthStore';

const teamMemberSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  role: z.string().min(1, 'Please select a role'),
  accessType: z.string().optional(),
  permissions: z.array(z.string()).min(1, 'At least one permission is required'),
  bulkTransferRight: z.preprocess((val) => (val === '' ? undefined : val), z.enum(['INITIATOR', 'APPROVER']).optional()),
});

const ROLE_OPTIONS = [
  { value: 'ADMIN', label: 'Admin' }
];

const ACCESS_TYPE_OPTIONS = [
  { value: 'VIEW_ONLY', label: 'View Only' },
  { value: 'MANAGE', label: 'Manage subscriptions, activate and deactivate' }
];

const BULK_TRANSFER_OPTIONS = [
  { value: 'INITIATOR', label: 'Initiator' },
  { value: 'APPROVER', label: 'Approver' }
];

const ROLE_PERMISSIONS = {
  VIEW_ONLY: [
    { value: PERMISSIONS.VIEW_TRANSACTIONS, label: 'View Transactions' },
    { value: PERMISSIONS.VIEW_PRODUCTS, label: 'View Products' },
    { value: PERMISSIONS.VIEW_API_KEYS, label: 'View API Keys' },
    { value: PERMISSIONS.VIEW_SETTINGS, label: 'View Settings' },
    { value: PERMISSIONS.DISPLAY, label: 'Display' },
    { value: PERMISSIONS.DOWNLOADS, label: 'Downloads' },
    { value: PERMISSIONS.EXPORT_TRANSACTIONS, label: 'Export Transactions' },
    { value: PERMISSIONS.SHARE, label: 'Share' }
  ],
  MANAGE: [
    { value: PERMISSIONS.VIEW_TRANSACTIONS, label: 'View Transactions' },
    { value: PERMISSIONS.VIEW_PRODUCTS, label: 'View Products' },
    { value: PERMISSIONS.VIEW_API_KEYS, label: 'View API Keys' },
    { value: PERMISSIONS.MANAGE_API_KEYS, label: 'Manage API Keys' },
    { value: PERMISSIONS.VIEW_SETTINGS, label: 'View Settings' },
    { value: PERMISSIONS.MANAGE_SETTINGS, label: 'Manage Settings' },
    { value: PERMISSIONS.MANAGE_WEBHOOKS, label: 'Manage Webhooks' },
    { value: PERMISSIONS.MANAGE_TEAM, label: 'Manage Team Members' },
    { value: PERMISSIONS.MANAGE_SUBSCRIPTIONS, label: 'Manage Product Subscriptions' },
    { value: PERMISSIONS.DOWNLOADS, label: 'Downloads' },
    { value: PERMISSIONS.EXPORT_TRANSACTIONS, label: 'Export Transactions' }
  ]
};

const ALL_PERMISSION_OPTIONS = [
  ...new Map([...ROLE_PERMISSIONS.VIEW_ONLY, ...ROLE_PERMISSIONS.MANAGE].map(item => [item.value, item])).values()
];

const getPermissionLabel = (value) => {
  return ALL_PERMISSION_OPTIONS.find(opt => opt.value === value)?.label || value;
};

const TeamPage = () => {
  const { user } = useAuthStore();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isDeactivatingMember, setIsDeactivatingMember] = useState(false);
  const [isViewingMember, setIsViewingMember] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [memberToDeactivate, setMemberToDeactivate] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetails, setMemberDetails] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(teamMemberSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'ADMIN',
      accessType: '',
      permissions: [],
      bulkTransferRight: '',
    },
  });

  const selectedAccessType = watch('accessType');

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    control: controlEdit,
    reset: resetEdit,
    watch: watchEdit,
    formState: { errors: errorsEdit },
  } = useForm({
    resolver: zodResolver(teamMemberSchema),
    mode: 'onChange',
  });

  const selectedAccessTypeEdit = watchEdit('accessType');

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchTeamMembers();
      const teamData = (response.data.data || []).filter(member => member.role !== 'OWNER');
      setTeam(teamData);
    } catch (error) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);
  const handleOpenAdd = () => {
    reset({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'ADMIN',
      accessType: '',
      permissions: [],
      bulkTransferRight: ''
    });
    setIsAddingMember(true);
  };

  const handleOpenDeactivate = (member) => {
    setMemberToDeactivate(member);
    setIsDeactivatingMember(true);
  };

  const handleRowClick = async (member) => {
    setSelectedMember(member);
    setIsViewingMember(true);
    setLoading(true);
    try {
      const response = await fetchTeamMemberDetails(member.userId);
      const details = response.data.data || response.data;
      setMemberDetails(details);

      const hasManagePerms = (details.permissions || []).some(p =>
        [PERMISSIONS.MANAGE_TEAM, PERMISSIONS.MANAGE_API_KEYS, PERMISSIONS.MANAGE_SETTINGS, PERMISSIONS.MANAGE_WEBHOOKS, PERMISSIONS.MANAGE_SUBSCRIPTIONS].includes(p)
      );
      const inferredAccessType = (details.permissions || []).length > 0 ? (hasManagePerms ? 'MANAGE' : 'VIEW_ONLY') : '';

      resetEdit({
        firstName: details.firstName || '',
        lastName: details.lastName || '',
        email: details.email || '',
        phone: details.phoneNumber || details.phone || '',
        role: details.role || 'ADMIN',
        accessType: inferredAccessType,
        permissions: details.permissions || [],
        bulkTransferRight: details.bulkTransferRight || '',
      });
    } catch (error) {
      toast.error('Failed to fetch member details');
      setIsViewingMember(false);
    } finally {
      setLoading(false);
    }
  };

  const onInviteSubmit = async (data) => {
    setIsProcessing(true);
    const submitData = {
      ...data,
      bulkTransferRight: data.bulkTransferRight || undefined,
      phone: data.phone || undefined,
      role: 'ADMIN',
      permissions: data.permissions || [],
    };
    delete submitData.accessType;

    try {
      const response = await inviteTeamMember(submitData);

      if (response.data?.status || response.data?.success || response.status === 200 || response.status === 201) {
        setIsAddingMember(false);
        toast.success('Team member invited successfully');
        loadTeam();
      } else {
        toast.error(response.data?.message || 'Failed to invite team member');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to invite team member');
    } finally {
      setIsProcessing(false);
    }
  };

  const onUpdateSubmit = async (data) => {
    if (!selectedMember) return;
    setIsProcessing(true);
    const submitData = {
      ...data,
      bulkTransferRight: data.bulkTransferRight || undefined,
      phone: data.phone || undefined,
      role: 'ADMIN',
    };
    delete submitData.accessType;

    try {
      await updateTeamMember(selectedMember.userId, submitData);
      toast.success('Member updated successfully');
      setIsEditing(false);
      // Refresh details
      const response = await fetchTeamMemberDetails(selectedMember.userId);
      setMemberDetails(response.data.data || response.data);
      loadTeam();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update member');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedMember) return;
    setIsProcessing(true);
    try {
      await deactivateTeamMember(selectedMember.userId);
      toast.success('Member deactivated successfully');
      // Refresh details
      const response = await fetchTeamMemberDetails(selectedMember.userId);
      setMemberDetails(response.data.data || response.data);
      loadTeam();
    } catch (error) {
      toast.error('Failed to deactivate member');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedMember) return;
    setIsProcessing(true);
    try {
      await reactivateTeamMember(selectedMember.userId);
      toast.success('Member reactivated successfully');
      const response = await fetchTeamMemberDetails(selectedMember.userId);
      setMemberDetails(response.data.data || response.data);
      loadTeam();
    } catch (error) {
      toast.error('Failed to reactivate member');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResendInvite = async () => {
    if (!selectedMember) return;
    setIsProcessing(true);
    try {
      await resendInvite(selectedMember.userId);
      toast.success('Invite resent successfully');
    } catch (error) {
      toast.error('Failed to resend invite');
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmDeactivateMember = async () => {
    if (!memberToDeactivate) return;

    const status = memberToDeactivate.status?.toUpperCase();
    const isCurrentlyActive = status === 'ACTIVE' || status === 'INVITED' || !status;


    setIsProcessing(true);
    try {
      if (isCurrentlyActive) {
        await deactivateTeamMember(memberToDeactivate.userId);
        toast.success('Member deactivated successfully');
      } else {
        await reactivateTeamMember(memberToDeactivate.userId);
        toast.success('Member reactivated successfully');
      }
      setIsDeactivatingMember(false);
      setMemberToDeactivate(null);
      loadTeam();
    } catch (error) {
      toast.error(`Failed to ${isCurrentlyActive ? 'deactivate' : 'reactivate'} member`);
    } finally {
      setIsProcessing(false);
    }

  };


  if (loading && team.length === 0) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Team Management</h2>
          <p className="text-sm text-slate-500 mt-1">Manage your team members and their operational roles.</p>
        </div>
        <Button onClick={handleOpenAdd} className="gap-2 shadow-sm">
          <Plus size={18} /> Add Team Member
        </Button>
      </div>

      <Card className="w-full overflow-hidden">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Users size={20} className="text-primary" />
            <CardTitle className="text-lg">Team Members</CardTitle>
          </div>
          <CardDescription>A list of all users on your team and their assigned permissions.</CardDescription>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50/30 border-b border-slate-100">
                <tr>
                  <th scope="col" className="px-6 py-4 font-semibold">Member</th>
                  <th scope="col" className="px-6 py-4 font-semibold">Email</th>
                  <th scope="col" className="px-6 py-4 font-semibold">Role</th>
                  <th scope="col" className="px-6 py-4 font-semibold">Permissions</th>
                  <th scope="col" className="px-6 py-4 font-semibold">Bulk Transfer Right</th>
                  <th scope="col" className="px-6 py-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {team.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <Users size={40} className="text-slate-200" />
                        <p>No team members found. Start by adding someone!</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  team.map((member) => {
                    const isCurrentUser = member.userId === user?.id;
                    return (
                      <tr
                        key={member.userId}
                        className={`transition-colors group ${isCurrentUser ? 'bg-slate-50/50 cursor-default' : 'hover:bg-slate-50 cursor-pointer'}`}
                        onClick={() => !isCurrentUser && handleRowClick(member)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white font-bold shrink-0 shadow-sm">
                              {member.firstName?.charAt(0)}{member.lastName?.charAt(0)}
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900">
                                {member.firstName} {member.lastName}
                                {isCurrentUser && <span className="ml-2 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full font-bold">You</span>}
                              </div>
                              <div className="text-xs text-slate-400 capitalize">{member.status?.toLowerCase()}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Mail size={12} className="text-slate-400" />
                            {member.email}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium bg-primary/5 text-primary border border-primary/20 rounded-full uppercase">
                            {ROLE_OPTIONS.find(opt => opt.value === member.role)?.label || member.role || 'VIEW ONLY'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {(member.permissions || []).slice(0, 2).map((perm, idx) => (
                              <span key={idx} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] whitespace-nowrap">
                                {getPermissionLabel(perm)}
                              </span>
                            ))}
                            {(member.permissions || []).length > 2 && (
                              <span className="text-[10px] text-slate-400">
                                +{(member.permissions || []).length - 2} more
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-xs font-semibold text-slate-600">
                          {BULK_TRANSFER_OPTIONS.find(opt => opt.value === member.bulkTransferRight)?.label || '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          {!isCurrentUser && (
                            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                              {(member.status?.toUpperCase() === 'ACTIVE' || member.status?.toUpperCase() === 'INVITED' || !member.status) ? (
                                <button
                                  onClick={() => handleOpenDeactivate(member)}
                                  className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                  title="Deactivate Member"
                                >
                                  <UserX size={16} />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleOpenDeactivate(member)}
                                  className="text-slate-400 hover:text-green-600 hover:bg-green-50 p-2 rounded-lg transition-colors"
                                  title="Reactivate Member"
                                >
                                  <UserCheck size={16} />
                                </button>
                              )}
                            </div>
                          )}
                        </td>

                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* --- MODALS --- */}

      {/* Add Member Modal */}
      <Modal
        isOpen={isAddingMember}
        onClose={() => setIsAddingMember(false)}
        title={
          <div className="flex items-center gap-2 text-slate-900 font-bold text-xl">
            <Plus className="text-primary" />
            <span>Add Team Member</span>
          </div>
        }
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button variant="secondary" onClick={() => setIsAddingMember(false)}>Cancel</Button>
            <Button
              form="invite-member-form"
              type="submit"
              isLoading={isProcessing}
            >
              Send Invite
            </Button>
          </div>
        }
      >
        <form id="invite-member-form" onSubmit={handleSubmit(onInviteSubmit)} className="pb-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="First Name"
              placeholder="John"
              {...register('firstName')}
              error={errors.firstName?.message}
            />
            <Input
              label="Last Name"
              placeholder="Doe"
              {...register('lastName')}
              error={errors.lastName?.message}
            />
          </div>

          <Input
            label="Email Address"
            type="email"
            placeholder="john.doe@company.com"
            {...register('email')}
            error={errors.email?.message}
          />

          <div className="space-y-4">
            <div className="space-y-4">
              <Controller
                name="role"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Dashboard Role"
                    placeholder="Select a role"
                    options={ROLE_OPTIONS}
                    error={errors.role?.message}
                    onChange={(val) => field.onChange(val.value)}
                    value={ROLE_OPTIONS.find(opt => opt.value === field.value)}
                  />
                )}
              />

              <Controller
                name="accessType"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="Access Type"
                    placeholder="Select access type"
                    options={ACCESS_TYPE_OPTIONS}
                    error={errors.accessType?.message}
                    onChange={(val) => {
                      field.onChange(val.value);
                      const defaultPerms = ROLE_PERMISSIONS[val.value]?.map(p => p.value) || [];
                      reset({ ...watch(), accessType: val.value, permissions: defaultPerms });
                    }}
                    value={ACCESS_TYPE_OPTIONS.find(opt => opt.value === field.value)}
                  />
                )}
              />
            </div>

            <Controller
              name="bulkTransferRight"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="Select a bulk transfer right"
                  label="Bulk Transfer Right"
                  options={BULK_TRANSFER_OPTIONS}
                  error={errors.bulkTransferRight?.message}
                  onChange={(val) => field.onChange(val.value)}
                  value={BULK_TRANSFER_OPTIONS.find(opt => opt.value === field.value)}
                />
              )}
            />
          </div>

          {selectedAccessType && (
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Select permissions for {selectedAccessType === 'VIEW_ONLY' ? 'view only' : 'manage'}</label>
              <div className="grid grid-cols-1 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                {(ROLE_PERMISSIONS[selectedAccessType] || []).map((permission) => (
                  <Controller
                    key={permission.value}
                    name="permissions"
                    control={control}
                    render={({ field }) => (
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <div
                          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${field.value?.includes(permission.value)
                            ? 'bg-primary border-primary text-white'
                            : 'bg-white border-slate-300 group-hover:border-primary'
                            }`}
                          onClick={() => {
                            const current = field.value || [];
                            if (current.includes(permission.value)) {
                              field.onChange(current.filter(p => p !== permission.value));
                            } else {
                              field.onChange([...current, permission.value]);
                            }
                          }}
                        >
                          {field.value?.includes(permission.value) && <Check size={14} />}
                        </div>
                        <span className="text-xs text-slate-600 font-medium">{permission.label}</span>
                      </label>
                    )}
                  />
                ))}
              </div>
              {errors.permissions && (
                <p className="text-xs text-red-500 mt-1">{errors.permissions.message}</p>
              )}
            </div>
          )}
        </form>
      </Modal>

      {/* Member Details Modal */}
      <Modal
        isOpen={isViewingMember}
        onClose={() => {
          setIsViewingMember(false);
          setIsEditing(false);
          setMemberDetails(null);
        }}
        title={
          <div className="flex items-center gap-2 text-slate-900 font-bold text-xl">
            <Users className="text-primary" />
            <span>Member Details</span>
          </div>
        }
        footer={
          <div className="flex gap-3 justify-end w-full">
            {!isEditing ? (
              <Button onClick={() => setIsEditing(true)}>Edit Member</Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setIsEditing(false)}>Cancel</Button>
                <Button
                  form="edit-member-form"
                  type="submit"
                  isLoading={isProcessing}
                >
                  Save Changes
                </Button>
              </>
            )}
            <Button variant="secondary" onClick={() => setIsViewingMember(false)}>Close</Button>
          </div>
        }
      >
        {loading ? (
          <div className="py-12 flex justify-center">
            <Spinner size="md" />
          </div>
        ) : memberDetails ? (
          <div className="space-y-6 pb-4">
            {!isEditing ? (
              <>
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white text-2xl font-bold shadow-sm">
                    {memberDetails.firstName?.charAt(0)}{memberDetails.lastName?.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">{memberDetails.firstName} {memberDetails.lastName}</h3>
                    <div className="flex gap-2 mt-1">
                      <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase border border-primary/20">
                        {ROLE_OPTIONS.find(opt => opt.value === memberDetails.role)?.label || memberDetails.role}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-bold uppercase border border-slate-200">
                        BT: {BULK_TRANSFER_OPTIONS.find(opt => opt.value === memberDetails.bulkTransferRight)?.label || 'N/A'}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${memberDetails.status?.toUpperCase() === 'ACTIVE' ? 'bg-green-50 text-green-700 border-green-200' :
                        memberDetails.status?.toUpperCase() === 'INVITED' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-red-50 text-red-700 border-red-200'
                        }`}>
                        {memberDetails.status}
                      </span>

                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Email Address</p>
                    <p className="text-slate-700 font-medium">{memberDetails.email}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Assigned Permissions</p>
                  <div className="flex flex-wrap gap-2">
                    {(memberDetails.permissions || []).map((perm, idx) => (
                      <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium border border-slate-200">
                        {getPermissionLabel(perm)}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100">
                  {(memberDetails.status?.toUpperCase() === 'ACTIVE' || memberDetails.status?.toUpperCase() === 'INVITED') && (
                    <Button variant="danger" size="sm" outline onClick={handleDeactivate} isLoading={isProcessing}>
                      Deactivate Member
                    </Button>
                  )}


                  {(memberDetails.status?.toUpperCase() === 'INACTIVE' || memberDetails.status?.toUpperCase() === 'DEACTIVATED') && (
                    <Button variant="success" size="sm" outline onClick={handleReactivate} isLoading={isProcessing}>
                      Reactivate Member
                    </Button>
                  )}

                  {memberDetails.status?.toUpperCase() === 'INVITED' && (
                    <Button variant="secondary" size="sm" onClick={handleResendInvite} isLoading={isProcessing}>
                      Resend Invite
                    </Button>
                  )}

                </div>
              </>
            ) : (
              <form id="edit-member-form" onSubmit={handleSubmitEdit(onUpdateSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-70">
                  <Input
                    label="First Name"
                    {...registerEdit('firstName')}
                    error={errorsEdit.firstName?.message}
                    disabled
                  />
                  <Input
                    label="Last Name"
                    {...registerEdit('lastName')}
                    error={errorsEdit.lastName?.message}
                    disabled
                  />
                </div>
                <Input
                  label="Email Address"
                  {...registerEdit('email')}
                  error={errorsEdit.email?.message}
                  disabled
                  className="opacity-70"
                />

                <div className="space-y-4">
                  <Controller
                    name="role"
                    control={controlEdit}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Dashboard Role"
                        placeholder="Select a role"
                        options={ROLE_OPTIONS}
                        error={errorsEdit.role?.message}
                        onChange={(val) => field.onChange(val.value)}
                        value={ROLE_OPTIONS.find(opt => opt.value === field.value)}
                      />
                    )}
                  />

                  <Controller
                    name="accessType"
                    control={controlEdit}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Access Type"
                        placeholder="Select access type"
                        options={ACCESS_TYPE_OPTIONS}
                        error={errorsEdit.accessType?.message}
                        onChange={(val) => {
                          field.onChange(val.value);
                          const defaultPerms = ROLE_PERMISSIONS[val.value]?.map(p => p.value) || [];
                          resetEdit({ ...watchEdit(), accessType: val.value, permissions: defaultPerms });
                        }}
                        value={ACCESS_TYPE_OPTIONS.find(opt => opt.value === field.value)}
                      />
                    )}
                  />
                </div>

                  <Controller
                    name="bulkTransferRight"
                    control={controlEdit}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Bulk Transfer Right"
                        options={BULK_TRANSFER_OPTIONS}
                        error={errorsEdit.bulkTransferRight?.message}
                        onChange={(val) => field.onChange(val.value)}
                        value={BULK_TRANSFER_OPTIONS.find(opt => opt.value === field.value)}
                      />
                    )}
                  />

                {selectedAccessTypeEdit && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Select permissions for {selectedAccessTypeEdit === 'VIEW_ONLY' ? 'view only' : 'manage'}</label>
                    <div className="grid grid-cols-1 gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      {(ROLE_PERMISSIONS[selectedAccessTypeEdit] || []).map((permission) => (
                        <Controller
                          key={permission.value}
                          name="permissions"
                          control={controlEdit}
                          render={({ field }) => (
                            <label className="flex items-center gap-2 cursor-pointer group">
                              <div
                                className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${field.value?.includes(permission.value)
                                  ? 'bg-primary border-primary text-white'
                                  : 'bg-white border-slate-300 group-hover:border-primary'
                                  }`}
                                onClick={() => {
                                  const current = field.value || [];
                                  if (current.includes(permission.value)) {
                                    field.onChange(current.filter(p => p !== permission.value));
                                  } else {
                                    field.onChange([...current, permission.value]);
                                  }
                                }}
                              >
                                {field.value?.includes(permission.value) && <Check size={14} />}
                              </div>
                              <span className="text-xs text-slate-600 font-medium">{permission.label}</span>
                            </label>
                          )}
                        />
                      ))}
                    </div>
                    {errorsEdit.permissions && (
                      <p className="text-xs text-red-500 mt-1">{errorsEdit.permissions.message}</p>
                    )}
                  </div>
                )}
              </form>
            )}
          </div>
        ) : (
          <div className="py-12 text-center text-slate-500">
            No details found for this member.
          </div>
        )}
      </Modal>

      {/* Deactivate Confirmation Modal */}
      <Modal
        isOpen={isDeactivatingMember}
        onClose={() => setIsDeactivatingMember(false)}
        title={
          memberToDeactivate && (
            <div className={`flex items-center gap-2 font-bold text-xl ${(memberToDeactivate.status?.toUpperCase() === 'ACTIVE' || memberToDeactivate.status?.toUpperCase() === 'INVITED' || !memberToDeactivate.status) ? 'text-red-600' : 'text-green-600'}`}>
              {(memberToDeactivate.status?.toUpperCase() === 'ACTIVE' || memberToDeactivate.status?.toUpperCase() === 'INVITED' || !memberToDeactivate.status) ? <UserX size={24} /> : <UserCheck size={24} />}
              <span>Confirm {(memberToDeactivate.status?.toUpperCase() === 'ACTIVE' || memberToDeactivate.status?.toUpperCase() === 'INVITED' || !memberToDeactivate.status) ? 'Deactivation' : 'Reactivation'}</span>
            </div>
          )
        }
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button variant="secondary" onClick={() => setIsDeactivatingMember(false)}>Cancel</Button>
            <Button
              variant={(memberToDeactivate?.status?.toUpperCase() === 'ACTIVE' || memberToDeactivate?.status?.toUpperCase() === 'INVITED' || !memberToDeactivate?.status) ? 'danger' : 'success'}
              onClick={confirmDeactivateMember}
              isLoading={isProcessing}
            >
              {(memberToDeactivate?.status?.toUpperCase() === 'ACTIVE' || memberToDeactivate?.status?.toUpperCase() === 'INVITED' || !memberToDeactivate?.status) ? 'Deactivate Member' : 'Reactivate Member'}
            </Button>
          </div>
        }
      >
        {memberToDeactivate ? (
          <div className="space-y-3">
            <p className="text-slate-600">
              Are you sure you want to {(memberToDeactivate.status?.toUpperCase() === 'ACTIVE' || memberToDeactivate.status?.toUpperCase() === 'INVITED' || !memberToDeactivate.status) ? 'deactivate' : 'reactivate'} <span className="font-bold text-slate-900">{memberToDeactivate.firstName} {memberToDeactivate.lastName}</span>?
            </p>
            <div className={`p-3 rounded-lg border flex gap-3 ${(memberToDeactivate.status?.toUpperCase() === 'ACTIVE' || memberToDeactivate.status?.toUpperCase() === 'INVITED' || !memberToDeactivate.status) ? 'bg-red-50 border-red-100' : 'bg-green-50 border-green-100'}`}>
              <AlertCircle size={18} className={`${(memberToDeactivate.status?.toUpperCase() === 'ACTIVE' || memberToDeactivate.status?.toUpperCase() === 'INVITED' || !memberToDeactivate.status) ? 'text-red-600' : 'text-green-600'} shrink-0 mt-0.5`} />
              <p className={`text-xs leading-relaxed ${(memberToDeactivate.status?.toUpperCase() === 'ACTIVE' || memberToDeactivate.status?.toUpperCase() === 'INVITED' || !memberToDeactivate.status) ? 'text-red-700' : 'text-green-700'}`}>
                {(memberToDeactivate.status?.toUpperCase() === 'ACTIVE' || memberToDeactivate.status?.toUpperCase() === 'INVITED' || !memberToDeactivate.status)
                  ? 'This action will revoke their access to the dashboard and related operations. You can reactivate them at any time.'
                  : 'This action will restore their access to the dashboard and related operations.'}
              </p>
            </div>
          </div>
        ) : null}
      </Modal>



    </div>
  );
};

export default TeamPage;

